import { IconChevronRight, IconLayoutSidebar } from "@tabler/icons-react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAnimatedSidebar } from "@/components/ui/animated-sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/auth";
import { AccountMenu } from "./account-menu";
import { HeaderScopeSlot } from "./header-scope-slot";
import { resolveNavTitleKey } from "./navigation";
import { NotificationsMenu } from "./notifications-menu";

export function SiteHeader(): React.ReactElement {
  return (
    <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-5">
      <div className="flex min-w-0 items-center gap-3">
        <SidebarToggle />
        <span aria-hidden className="hidden h-5 w-px shrink-0 bg-line md:block" />
        <HeaderBreadcrumb />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <HeaderScopeSlot className="peer flex items-center empty:hidden" />
        <span aria-hidden className="h-5 w-px shrink-0 bg-line peer-empty:hidden" />
        <NotificationsMenu />
        <AccountMenu />
      </div>
    </header>
  );
}

function HeaderBreadcrumb(): React.ReactElement {
  const { t } = useTranslation("layout");
  const { pathname } = useLocation();
  const workspaceName = useAuthStore((state) => {
    const workspaces = state.user?.workspaces ?? [];
    return (
      workspaces.find((workspace) => workspace.id === state.user?.activeWorkspaceId) ??
      workspaces[0]
    )?.name;
  });
  const titleKey = resolveNavTitleKey(pathname);

  return (
    <nav aria-label={t("breadcrumb.ariaLabel")} className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-base">
        {workspaceName ? (
          <li className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-muted-foreground">{workspaceName}</span>
            {titleKey ? (
              <IconChevronRight
                className="size-3.5 shrink-0 text-placeholder"
                stroke={2}
                aria-hidden
              />
            ) : null}
          </li>
        ) : null}
        {titleKey ? (
          <li aria-current="page" className="truncate font-medium text-ink">
            {t(titleKey)}
          </li>
        ) : null}
      </ol>
    </nav>
  );
}

export function SidebarToggle(): React.ReactElement {
  const { t } = useTranslation("layout");
  const { expanded, toggle } = useAnimatedSidebar();
  const label = expanded ? t("nav.collapseSidebar") : t("nav.expandSidebar");

  return (
    <Tooltip>
      <TooltipTrigger
        aria-label={label}
        aria-expanded={expanded}
        render={<button type="button" onClick={toggle} />}
        className="hidden size-8 shrink-0 items-center justify-center rounded-xs text-ink outline-none transition-colors hover:bg-state-hover active:bg-state-pressed focus-visible:ring-2 focus-visible:ring-focus-ring md:flex"
      >
        <IconLayoutSidebar className="size-4" stroke={1.75} aria-hidden />
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {label} · {t("nav.shortcut")}
      </TooltipContent>
    </Tooltip>
  );
}
